import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import CreateAd from "./CreateAd";
import { getAdSet, getCampaign, getAdAccount, createAd, submitAd } from "../../hook/useAdsApi";
import { getCurrentUserId, PLACEMENTS, daysBetween } from "./adsConstants";

const CheckBox = ({ checked }) => (
  <span
    className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${
      checked ? "bg-[#095228]" : "border-2 border-[#c3c5d9]"
    }`}
  >
    {checked && (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
        <path d="M5 13l4 4L19 7" />
      </svg>
    )}
  </span>
);

const STEP_TITLES = ["Create Ad", "Ad Placement"];

export default function CreateAdPage() {
  const { adSetId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [campaign, setCampaign] = useState(null);
  const [adAccount, setAdAccount] = useState(null);
  const [formData, setFormData] = useState({
    adName: "",
    creativeType: "image",
    mediaFiles: [],
    headline: "",
    description: "",
    cta: "",
    destinationType: "profile",
    destinationId: getCurrentUserId(),
    objective: null,
    placements: { searchResult: false, marketplaceFeed: false, categoryResults: false },
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // getAdSet() doesn't return the campaign's objective (only campaign_id), so a second
    // call is needed - CreateAd needs the objective to filter valid CTAs/destinations, and
    // the campaign's budget/dates are needed here too to auto-compute delivery_config's
    // maximum_bid on step 2.
    getAdSet(adSetId)
      .then((adSet) => getCampaign(adSet.campaign_id))
      .then((campaignResult) => {
        setCampaign(campaignResult);
        setFormData((prev) => ({ ...prev, objective: campaignResult.objective }));
        // Needed so CreateAd can hide Product/Marketplace Listing destination for Creator/Brand
        // and External Business accounts, which have no seller_id and so no products.
        return getAdAccount(campaignResult.ad_account_id);
      })
      .then(setAdAccount)
      .catch(() => toast.error("Failed to load ad set", { toastId: "create-ad-page-load-error" }))
      .finally(() => setLoading(false));
  }, [adSetId]);

  const updateFormData = (patch) => setFormData((prev) => ({ ...prev, ...patch }));
  const goNext = () => setStep(2);
  const goBack = () => setStep(1);

  const togglePlacement = (key) => {
    updateFormData({ placements: { ...formData.placements, [key]: !formData.placements[key] } });
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const selectedPlacements = PLACEMENTS.filter((p) => formData.placements[p.key]);
      // delivery_config is what the buyer-facing auction engine (ad-auction.service.js) reads
      // for eligibility - only include it when at least one placement was picked, since the
      // backend requires maximum_bid + a non-empty placements array whenever it's provided at
      // all. maximum_bid is a placeholder heuristic (same as the main wizard's and
      // promoteListing's) - a fraction of the campaign's daily budget, never surfaced to the
      // seller since this UI doesn't expose real bidding yet.
      const days = campaign ? daysBetween(campaign.start_date, campaign.end_date) || 1 : 1;
      const dailyAmount = campaign ? Number(campaign.budget) / days : 0;
      const deliveryConfig = selectedPlacements.length
        ? {
            maximum_bid: Math.max(0.01, Number((dailyAmount * 0.05).toFixed(6))),
            placements: selectedPlacements.map((p) => p.deliveryValue),
          }
        : undefined;

      const ad = await createAd(adSetId, {
        name: formData.adName,
        creative_type: formData.creativeType,
        creative_media: formData.mediaFiles,
        thumbnail: formData.creativeType === "video" ? formData.thumbnail : formData.mediaFiles[0],
        headline: formData.headline,
        body_text: formData.description,
        cta: formData.cta,
        destination_type: formData.destinationType,
        destination_id: formData.destinationId || undefined,
        ...(deliveryConfig && { delivery_config: deliveryConfig }),
      });
      const submitted = await submitAd(ad.id);
      toast.success("Ad created and submitted for review");
      navigate(`/ads/ads/${submitted?.id || ad.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create ad");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => (step === 1 ? navigate(-1) : goBack())}
                className="rounded-full bg-[#e7f3ea] p-2"
              >
                ←
              </button>
              <h1 className="text-xl font-bold">{STEP_TITLES[step - 1]}</h1>
            </div>
            {loading || !formData.objective ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : step === 1 ? (
              <CreateAd
                formData={formData}
                updateFormData={updateFormData}
                onNext={goNext}
                onBack={() => navigate(-1)}
                adAccount={adAccount}
                continueLabel="Continue"
              />
            ) : (
              <div>
                <label className="block text-sm font-semibold text-black mb-2">Select Placement</label>
                <div className="space-y-3 mb-8">
                  {PLACEMENTS.map((p) => (
                    <label
                      key={p.key}
                      className="flex items-start gap-3 bg-[#fefdeb] border border-[rgba(195,197,217,0.6)] rounded-2xl p-4 cursor-pointer"
                    >
                      <div className="mt-0.5" onClick={() => togglePlacement(p.key)}>
                        <CheckBox checked={!!formData.placements[p.key]} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#131b2e]">{p.label}</span>
                          {p.badge && (
                            <span className="bg-[#eef000] text-[10px] font-bold px-2 py-0.5 rounded-full">
                              {p.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#747474] mt-0.5">{p.description}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goBack}
                    className="border border-[#c3c5d9] text-[#434656] font-medium text-lg px-8 py-3 rounded-full"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleCreate}
                    className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
                  >
                    {submitting ? "Saving..." : "Create & Submit for Review"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
